import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import {
    ArrowLeft,
    Calendar,
    Eye,
    Briefcase,
    Copy,
    Check,
    Star,
    CheckCircle2,
    AlertTriangle,
    Building2,
    Award,
    FileDown,
    Loader2,
    Pencil,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
};

const ASSIDUITE_LABELS = {
    present_no_absence: { label: "Toujours présent, aucune absence", tone: "ok" },
    present_some: { label: "Quelques absences justifiées", tone: "warn" },
    absences_concerning: { label: "Absences préoccupantes", tone: "bad" },
};

const AUTONOMIE_LABELS = {
    assiste: "Doit être assisté en permanence",
    encadre: "Encadré, demande ponctuellement",
    autonome: "Travaille en totale autonomie",
};

export default function BilanDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [bilan, setBilan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isFormateur = user?.role === "formateur" || user?.role === "admin";
    const isResponsable = user?.role === "responsable" || user?.role === "admin";
    const isOwner = bilan && bilan.user_id === user?.id;
    // Anyone of the establishment (alternant author, formateur, responsable, admin) can delete.
    const canDelete = isOwner || isResponsable || user?.role === "formateur";
    const printRef = useRef(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/bilans/${id}`);
                setBilan(data);
            } catch (e) { /* ignore */ }
            finally { setLoading(false); }
        })();
    }, [id]);

    const tutorLink = useMemo(() => {
        if (!bilan?.tutor_token) return "";
        return `${window.location.origin}/tuteur/${bilan.id}/${bilan.tutor_token}`;
    }, [bilan]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(tutorLink);
            setCopied(true);
            toast.success("Lien copié !");
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("Impossible de copier");
        }
    };

    const removeBilan = async () => {
        if (!bilan) return;
        const msg = isOwner
            ? "Supprimer définitivement ce bilan ? Les photos et données seront perdues."
            : `Supprimer définitivement le bilan de ${bilan.prenom} ${bilan.nom} ? Cette action ne peut pas être annulée.`;
        if (!window.confirm(msg)) return;
        setDeleting(true);
        try {
            await api.delete(`/bilans/${bilan.id}`);
            toast.success("Bilan supprimé");
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suppression impossible");
        } finally {
            setDeleting(false);
        }
    };

    const downloadPdf = async () => {
        if (!printRef.current || !bilan) return;
        setExporting(true);
        try {
            const html2pdf = (await import("html2pdf.js")).default;
            const filename = `Bilan_${bilan.prenom}_${bilan.nom}_${bilan.date_debut}.pdf`.replace(/\s+/g, "_");
            await html2pdf()
                .from(printRef.current)
                .set({
                    margin: [10, 10, 10, 10],
                    filename,
                    image: { type: "jpeg", quality: 0.95 },
                    html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                    pagebreak: { mode: ["avoid-all", "css", "legacy"] },
                })
                .save();
            toast.success("PDF téléchargé !");
        } catch (e) {
            toast.error("Erreur lors de la génération du PDF");
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center text-muted2">Chargement…</div>
            </div>
        );
    }

    if (!bilan) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                    <h1 className="h-display text-2xl">Bilan introuvable</h1>
                    <Link to="/dashboard" className="btn-primary mt-4 inline-flex">Retour</Link>
                </div>
            </div>
        );
    }

    const evalDone = !!bilan.tutor_evaluation;
    // Owner (alternant) and formateur both see the tutor LINK (alternant shares
    // it with their tutor). But ONLY the formateur sees the tutor's evaluation
    // result — the alternant must never see what the tutor scored on their bilan.
    const showTutorLink = (isOwner || isFormateur) && !!bilan.tutor_token;
    const showTutorEvaluation = isFormateur && evalDone;

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="bilan-detail-page">
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                    <Link to="/dashboard" className="text-sm font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink" data-testid="bilan-back-link">
                        <ArrowLeft size={14} /> Retour au tableau de bord
                    </Link>
                    <div className="flex items-center gap-2">
                        {isOwner && (
                            <Link
                                to={`/bilans/nouveau?edit=${bilan.id}`}
                                className="btn-outline !px-4 !py-2 text-xs"
                                data-testid="bilan-edit-button"
                            >
                                <Pencil size={14} /> Modifier
                            </Link>
                        )}
                        <button
                            onClick={downloadPdf}
                            disabled={exporting}
                            className="btn-secondary !px-4 !py-2 text-xs"
                            data-testid="bilan-download-pdf-button"
                        >
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                            {exporting ? "Génération…" : "Télécharger en PDF"}
                        </button>
                        {canDelete && (
                            <button
                                onClick={removeBilan}
                                disabled={deleting}
                                className="btn-outline !px-4 !py-2 text-xs !border-destructive/40 !text-destructive hover:!bg-destructive/10"
                                data-testid="bilan-delete-button"
                            >
                                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                Supprimer
                            </button>
                        )}
                    </div>
                </div>

                <div ref={printRef}><header className="border-b-4 border-orange pb-6 mb-8">
                    <div className="label-eyebrow mb-2">Bilan d'alternance · 2 semaines</div>
                    <h1 className="h-display text-4xl font-semibold">{bilan.prenom} {bilan.nom}</h1>
                    <div className="text-sm text-muted2 mt-2">
                        {FILIERE_LABELS[bilan.filiere]} — {bilan.niveau === "1ere" ? "1ère" : "2ème"} année
                    </div>
                    {isFormateur && bilan.author_email && (
                        <div className="text-xs font-mono text-muted2 mt-2 inline-flex items-center gap-1.5">
                            <Eye size={12} /> Soumis par {bilan.author_email} · {new Date(bilan.created_at).toLocaleDateString("fr-FR")}
                        </div>
                    )}
                </header>

                <div className="grid sm:grid-cols-3 gap-px bg-rule border border-rule mb-8">
                    <div className="bg-paper p-4">
                        <div className="label-eyebrow flex items-center gap-1.5"><Building2 size={11} /> Entreprise</div>
                        <div className="mt-1 text-sm font-medium">{bilan.entreprise || "—"}</div>
                    </div>
                    <div className="bg-paper p-4">
                        <div className="label-eyebrow">Tuteur</div>
                        <div className="mt-1 text-sm font-medium">{bilan.tuteur || "—"}</div>
                    </div>
                    <div className="bg-paper p-4">
                        <div className="label-eyebrow">Période</div>
                        <div className="mt-1 text-sm font-medium flex items-center gap-1.5">
                            <Calendar size={13} /> {bilan.date_debut} → {bilan.date_fin}
                        </div>
                    </div>
                </div>

                {/* TUTOR LINK CARD */}
                {showTutorLink && (
                    <div data-html2canvas-ignore="true" className={`panel p-6 mb-10 ${evalDone ? "" : "border-2 border-orange/40 bg-orange-50/40"}`} data-testid="tutor-link-card">
                        <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 shrink-0 flex items-center justify-center ${evalDone ? "bg-emerald-100 text-emerald-700" : "bg-orange text-white"}`}>
                                {evalDone ? <CheckCircle2 size={18} /> : <Briefcase size={18} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="label-eyebrow mb-1">Espace tuteur</div>
                                <h2 className="h-display text-xl font-semibold">
                                    {evalDone
                                        ? "Évaluation du tuteur reçue ✓"
                                        : "Partagez ce lien avec votre tuteur d'entreprise"}
                                </h2>
                                {!evalDone && (
                                    <p className="text-sm text-ink/70 mt-2">
                                        Le tuteur ouvre le lien (sans avoir besoin de compte), remplit son évaluation, valide. Vous et votre formateur la verrez ensuite ici.
                                    </p>
                                )}
                                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                                    <input
                                        readOnly
                                        value={tutorLink}
                                        className="field-input flex-1 font-mono text-xs"
                                        onFocus={(e) => e.target.select()}
                                        data-testid="tutor-link-input"
                                    />
                                    <button onClick={copyLink} className="btn-secondary !px-4" data-testid="tutor-link-copy">
                                        {copied ? <Check size={14} /> : <Copy size={14} />}
                                        {copied ? "Copié" : "Copier"}
                                    </button>
                                </div>
                                {!evalDone && (
                                    <p className="text-[11px] text-muted2 mt-3 font-mono">
                                        Astuce : envoyez ce lien à votre tuteur par e-mail, SMS ou WhatsApp.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* TUTOR EVALUATION (visible only to formateur/responsable) */}
                {showTutorEvaluation && <TutorEvaluationDisplay evaluation={bilan.tutor_evaluation} />}

                <Section number="01" title="Compétences évaluées (par l'alternant)">
                    {bilan.selected_competencies?.length ? (
                        <div className="border border-rule">
                            {bilan.selected_competencies.map((c) => (
                                <div key={c.code} className="flex gap-3 px-4 py-2.5 border-b border-rule last:border-b-0">
                                    <span className="font-mono text-xs text-orange font-bold w-12 shrink-0">{c.code}</span>
                                    <span className="text-sm">{c.label}</span>
                                </div>
                            ))}
                        </div>
                    ) : <Empty />}
                </Section>

                <Section number="02" title="Outils & logiciels">
                    {bilan.tools?.length ? (
                        <div className="flex flex-wrap gap-2">
                            {bilan.tools.map((t) => <span key={t} className="pill bg-ink text-white">{t}</span>)}
                        </div>
                    ) : <Empty />}
                </Section>

                <Section number="03" title="Activités réalisées">
                    {bilan.activities ? <Pre>{bilan.activities}</Pre> : <Empty />}
                </Section>

                <Section number="04" title="Difficultés rencontrées">
                    {bilan.difficulties ? <Pre>{bilan.difficulties}</Pre> : <Empty />}
                </Section>

                <Section number="05" title="Photos">
                    {bilan.photo_paths?.length ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="bilan-photos-grid">
                            {bilan.photo_paths.map((p) => (
                                <a key={p} href={`${window.location.origin}/api/files/${p}`} target="_blank" rel="noreferrer" className="block border border-rule">
                                    <img
                                        src={`${window.location.origin}/api/files/${p}`}
                                        alt="Photo activité"
                                        className="w-full h-40 object-cover"
                                    />
                                </a>
                            ))}
                        </div>
                    ) : <Empty />}
                </Section>
                </div>
            </main>
        </div>
    );
}

const TutorEvaluationDisplay = ({ evaluation: ev }) => {
    const ass = ASSIDUITE_LABELS[ev.assiduite];
    return (
        <section className="panel p-6 mb-10 border-l-4 border-l-orange" data-testid="tutor-evaluation-display">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-orange mb-1">Évaluation du tuteur d'entreprise</div>
                    <h2 className="h-display text-2xl font-semibold">
                        {ev.tutor_prenom} {ev.tutor_nom}
                    </h2>
                    {ev.tutor_fonction && <div className="text-sm text-muted2 mt-0.5">{ev.tutor_fonction}</div>}
                    {ev.submitted_at && (
                        <div className="text-[11px] text-muted2 mt-2 font-mono">
                            Reçu le {new Date(ev.submitted_at).toLocaleString("fr-FR")}
                        </div>
                    )}
                </div>
                {ev.note_globale != null && (
                    <div className="text-right">
                        <div className="label-eyebrow flex items-center justify-end gap-1.5"><Award size={11} /> Note globale</div>
                        <div className="h-display text-4xl font-bold text-orange">{ev.note_globale}<span className="text-base text-muted2">/20</span></div>
                    </div>
                )}
            </div>

            {/* Assiduité & autonomie */}
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
                {ass && (
                    <div className={`p-3 border rounded-sm ${ass.tone === "ok" ? "border-emerald-200 bg-emerald-50" : ass.tone === "warn" ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"}`}>
                        <div className="label-eyebrow mb-1">Assiduité</div>
                        <div className="text-sm font-medium flex items-center gap-1.5">
                            {ass.tone === "ok" ? <CheckCircle2 size={14} className="text-emerald-700" /> : <AlertTriangle size={14} className={ass.tone === "warn" ? "text-amber-700" : "text-red-700"} />}
                            {ass.label}
                        </div>
                        {ev.nb_absences != null && ev.nb_absences > 0 && (
                            <div className="text-xs text-muted2 mt-1">{ev.nb_absences} absence(s)</div>
                        )}
                    </div>
                )}
                {ev.autonomie && (
                    <div className="p-3 border border-rule rounded-sm">
                        <div className="label-eyebrow mb-1">Niveau d'autonomie</div>
                        <div className="text-sm font-medium">{AUTONOMIE_LABELS[ev.autonomie]}</div>
                    </div>
                )}
            </div>

            {/* Ratings */}
            {(ev.ponctualite || ev.serieux_motivation || ev.integration_equipe || ev.respect_delais) && (
                <div className="mt-5">
                    <div className="label-eyebrow mb-2">Comportement professionnel</div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <RatingRow label="Ponctualité" v={ev.ponctualite} />
                        <RatingRow label="Sérieux & motivation" v={ev.serieux_motivation} />
                        <RatingRow label="Intégration dans l'équipe" v={ev.integration_equipe} />
                        <RatingRow label="Respect consignes & délais" v={ev.respect_delais} />
                    </div>
                </div>
            )}

            {/* Text blocks */}
            <div className="mt-5 space-y-4">
                {ev.missions_realisees && <Block label="Missions réalisées">{ev.missions_realisees}</Block>}
                {(ev.competences_techniques || ev.competences_transversales) && (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {ev.competences_techniques && <Block label="Compétences techniques">{ev.competences_techniques}</Block>}
                        {ev.competences_transversales && <Block label="Compétences transversales">{ev.competences_transversales}</Block>}
                    </div>
                )}
                {ev.progression && <Block label="Progression">{ev.progression}</Block>}
                {ev.difficultes_amelioration && <Block label="Difficultés & amélioration">{ev.difficultes_amelioration}</Block>}
                {ev.appreciation_generale && <Block label="Appréciation générale" highlight>{ev.appreciation_generale}</Block>}
                {(ev.points_forts || ev.points_a_ameliorer) && (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {ev.points_forts && <Block label="Points forts" tone="ok">{ev.points_forts}</Block>}
                        {ev.points_a_ameliorer && <Block label="Points à améliorer" tone="warn">{ev.points_a_ameliorer}</Block>}
                    </div>
                )}
                {ev.commentaire_ecole && <Block label="Commentaire pour l'établissement">{ev.commentaire_ecole}</Block>}
            </div>

            {/* Signature */}
            {ev.signature && (
                <div className="mt-6 pt-4 border-t border-rule flex items-center justify-between text-xs">
                    <span className="text-muted2 font-mono">Signature électronique</span>
                    <span className="font-mono italic">{ev.signature}</span>
                </div>
            )}
        </section>
    );
};

const RatingRow = ({ label, v }) => (
    <div className="flex items-center justify-between py-1">
        <span className="text-muted2">{label}</span>
        <span className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={12} className={n <= (v || 0) ? "text-orange" : "text-rule"} fill={n <= (v || 0) ? "currentColor" : "none"} />
            ))}
            {v ? <span className="ml-1.5 font-mono text-[11px] text-muted2">{v}/5</span> : null}
        </span>
    </div>
);

const Block = ({ label, children, tone, highlight = false }) => {
    const border = tone === "ok" ? "border-l-emerald-500" : tone === "warn" ? "border-l-amber-500" : "border-l-orange";
    const bg = highlight ? "bg-surface" : "";
    return (
        <div className={`border-l-2 ${border} pl-4 py-1 ${bg}`}>
            <div className="label-eyebrow mb-1">{label}</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{children}</div>
        </div>
    );
};

const Section = ({ number, title, children }) => (
    <section className="mt-10">
        <div className="flex items-baseline gap-3 mb-3">
            <span className="text-xs font-mono text-orange">{number}</span>
            <h2 className="h-display text-xl font-semibold">{title}</h2>
        </div>
        {children}
    </section>
);

const Pre = ({ children }) => (
    <div className="text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-orange pl-4 py-1">{children}</div>
);

const Empty = () => <div className="text-sm text-muted2 italic">— Aucune donnée —</div>;
